import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, FileText } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { BuscarMovimiento } from "@/components/documentos/buscar-movimiento";
import { type Papel, Visor } from "@/components/propuesta/visor";
import { Lado } from "@/components/revision/lado";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

// ===========================================================================
// LA COLA POR BOLETA: los documentos de clientes que falta vincular.
//
// La otra cola —la de abonos— se arma desde los movimientos que tienen
// candidato, y con eso se quedaba VACÍA teniendo catorce expedientes esperando:
// el motor cierra la puerta cuando el voucher no explica el total de la boleta
// —un pago a plazos, por ejemplo— y esas boletas no salían en ningún contador
// ni en ninguna pantalla. La revisión decía «no queda nada por revisar».
//
// Contabilidad lo dijo así: «se supone que ahí tienen que aparecer todos los
// documentos de los clientes que tienen que estar vinculados a un movimiento
// bancario; esa es la cola». Y es la buena: son catorce y no ciento doce, son
// finitas, y son las que impiden cerrar el mes.
//
// Cuando el motor no propone nada se dice POR QUÉ y se ofrece buscarlo a mano.
// Un «no hay nada» sin motivo no es una pantalla vacía: es una tarea escondida.
// ===========================================================================

type Voucher = {
  recorte: string;
  importe: number | null;
  moneda: string | null;
  fechaPago: string | null;
  operacion: string | null;
  banco: string | null;
  medio: string | null;
  leido: boolean;
};

type Candidato = {
  movimientoId: string;
  fecha: string;
  importe: number;
  moneda: "PEN" | "USD";
  banco: string;
  descripcion: string | null;
  operacion: string | null;
  motivo: string | null;
  confianza: string | null;
  puntaje: string;
};

const dinero = (c: number | null, m: string | null) =>
  c === null
    ? "—"
    : `${m === "USD" ? "US$" : "S/"} ${(c / 100).toLocaleString("es-PE", {
        minimumFractionDigits: 2,
      })}`;

export function ColaDeBoletas() {
  const [indice, setIndice] = useState(0);
  const [buscando, setBuscando] = useState(false);
  /**
   * Cuál de los candidatos se está mirando.
   *
   * DE UNO EN UNO y no en lista: enfrentar el voucher con UN abono es la
   * pregunta que se responde —«¿es este?»— y una lista de ocho invita a
   * compararlos entre sí, que no es lo que hay que hacer.
   */
  const [iCand, setICand] = useState(0);
  const [papel, setPapel] = useState<Papel | null>(null);
  const qc = useQueryClient();
  const decidir = api.decidir.useMutation();

  const { data, isPending } = api.revisionBoletas.useQuery(
    ["revision-boletas", String(indice)],
    { query: { indice } },
  );
  const d = data?.status === 200 ? data.body : null;
  const caso = d?.caso ?? null;

  if (isPending) return <Skeleton className="mt-6 h-96 w-full" />;

  if (!d || d.total === 0)
    return (
      <div className="mt-10 text-center">
        <p className="font-semibold text-xl">
          No queda ninguna boleta por vincular
        </p>
        <p className="mt-2 text-muted-foreground">
          Todos los documentos de clientes de este mes tienen su abono.
        </p>
      </div>
    );
  if (!caso) return null;

  const conciliar = (movimientoId: string) => {
    decidir.mutate(
      {
        body: {
          movimientoId,
          comprobanteId: caso.comprobanteId,
          respuesta: "si",
        },
      },
      {
        onSuccess: (r: { status: number; body: { dice?: string } }) => {
          if (r.status !== 200) {
            toast.error("No se pudo conciliar");
            return;
          }
          toast.success(r.body.dice ?? "Conciliado y firmado");
          setBuscando(false);
          qc.invalidateQueries();
        },
        onError: () => toast.error("No se pudo conciliar"),
      },
    );
  };

  const falta = (caso.importe ?? 0) - caso.yaAplicado;
  const elVoucher: Voucher | undefined = caso.vouchers[0];
  const enCurso: Candidato | undefined = caso.candidatos[iCand];

  return (
    <div className="mt-4">
      {/* Dónde estoy en la cola. Va arriba porque lo primero que se pregunta
          quien revisa es cuánto queda. */}
      <div className="flex flex-wrap items-center gap-2 border-b pb-2 text-[12.5px]">
        <span className="text-muted-foreground tabular-nums">
          {indice + 1} de {d.total}
        </span>
        <span className="text-border">·</span>
        <span className="text-muted-foreground">
          boletas por vincular a un abono
        </span>
        <div className="ml-auto flex items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            disabled={indice === 0}
            onClick={() => {
              setBuscando(false);
              setICand(0);
              setIndice((n) => Math.max(0, n - 1));
            }}
          >
            <ChevronLeft className="size-3.5" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={indice + 1 >= d.total}
            onClick={() => {
              setBuscando(false);
              setICand(0);
              setIndice((n) => n + 1);
            }}
          >
            <ChevronRight className="size-3.5" />
          </Button>
        </div>
      </div>

      <div className="mt-6 text-center">
        <h2 className="font-semibold text-2xl tracking-tight">
          ¿Qué abono del banco pagó esta boleta?
        </h2>
        {caso.porQueNoPropone ? (
          <p className="mx-auto mt-2 max-w-2xl text-[13px] text-muted-foreground">
            El motor no propuso ninguno: {caso.porQueNoPropone}.
          </p>
        ) : null}
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <Lado
          titulo="La boleta"
          importe={dinero(caso.importe, caso.moneda)}
          filas={[
            ["Serie-número", caso.serieNumero ?? "—"],
            ["Cliente", caso.cliente ?? "—"],
            ["Fecha de pago", caso.fecha ?? "—"],
            ...(caso.yaAplicado > 0
              ? [
                  ["Ya cobrado", dinero(caso.yaAplicado, caso.moneda)],
                  ["Falta", dinero(falta, caso.moneda)],
                ]
              : []),
          ]}
          nota={
            caso.estado === "parcial"
              ? {
                  texto: "Va a plazos: parte ya está cobrada y falta el resto.",
                  tono: "warn",
                }
              : null
          }
        />

        {caso.vouchers.length > 0 ? (
          <div className="space-y-2">
            {/* EL VOUCHER, LA IMAGEN. Es el papel que trae el cliente, y sin
                verlo el cuadro de al lado es la palabra del lector óptico
                contra nada. Se agranda al pulsarlo. */}
            <button
              type="button"
              onClick={() =>
                elVoucher &&
                setPapel({
                  clase: "imagen",
                  recorte: elVoucher.recorte,
                  titulo: "El voucher del cliente",
                })
              }
              className="block w-full cursor-zoom-in rounded-lg border bg-card p-2"
            >
              <img
                src={`/imagen/${elVoucher?.recorte}`}
                alt="El voucher del cliente"
                className="h-44 w-full rounded bg-muted object-contain"
              />
            </button>
            <Lado
              titulo="Dice el voucher del cliente"
              importe={dinero(
                caso.vouchers[0]?.importe ?? null,
                caso.vouchers[0]?.moneda ?? caso.moneda,
              )}
              etiqueta={caso.vouchers[0]?.medio}
              filas={[
                ["Cuándo", caso.vouchers[0]?.fechaPago ?? "—"],
                ["N.º operación", caso.vouchers[0]?.operacion ?? "—"],
                ["Banco", caso.vouchers[0]?.banco ?? "—"],
                ...(caso.vouchers.length > 1
                  ? [["Vouchers", `${caso.vouchers.length} en el expediente`]]
                  : []),
              ]}
              nota={
                caso.vouchers.some((v: Voucher) => !v.leido)
                  ? {
                      texto:
                        "Alguna de sus imágenes no la ha leído el modelo. Se lee desde Docs. de clientes.",
                      tono: "warn",
                    }
                  : null
              }
            />
          </div>
        ) : (
          <div className="flex items-center justify-center rounded-lg border border-dashed p-8 text-center text-muted-foreground text-sm">
            Este expediente no trae ningún voucher legible.
          </div>
        )}
      </div>

      {/* EL CANDIDATO QUE SE MIRA, de uno en uno.
          Nada se aplica solo: se enfrenta el voucher con UN abono y lo firma
          quien mira. Pasar al siguiente no es una decisión y no queda escrito;
          descartar sí, y para eso está la mesa. */}
      {enCurso ? (
        <div className="mt-4 overflow-hidden rounded-lg border">
          <div className="flex flex-wrap items-center gap-2 border-b bg-muted/40 px-3 py-2">
            <span className="font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
              El abono que propone el motor
            </span>
            {caso.candidatos.length > 1 ? (
              <span className="text-[11.5px] text-muted-foreground tabular-nums">
                candidato {iCand + 1} de {caso.candidatos.length}
              </span>
            ) : null}
            {enCurso.confianza ? (
              <span className="rounded-full border px-2 py-0.5 text-[10.5px] text-muted-foreground uppercase tracking-wide">
                {enCurso.confianza}
              </span>
            ) : null}
            {caso.candidatos.length > 1 ? (
              <div className="ml-auto flex items-center gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={iCand === 0}
                  onClick={() => setICand((n) => Math.max(0, n - 1))}
                >
                  <ChevronLeft className="size-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={iCand + 1 >= caso.candidatos.length}
                  onClick={() => setICand((n) => n + 1)}
                >
                  <ChevronRight className="size-3.5" />
                </Button>
              </div>
            ) : null}
          </div>

          <div className="px-4 py-3">
            <div className="font-bold text-2xl tabular-nums tracking-tight">
              {dinero(enCurso.importe, enCurso.moneda)}
            </div>
            <dl className="mt-3 space-y-2 text-[12.5px]">
              {[
                ["Cuándo", enCurso.fecha],
                ["Dónde", enCurso.banco],
                ["N.º operación", enCurso.operacion ?? "—"],
                ["Concepto", enCurso.descripcion ?? "—"],
              ].map(([k, v]) => (
                <div
                  key={k}
                  className="flex items-baseline justify-between gap-4"
                >
                  <dt className="shrink-0 text-muted-foreground">{k}</dt>
                  <dd
                    className={cn(
                      "truncate text-right font-medium",
                      k === "N.º operación" && "font-mono text-[12px]",
                    )}
                    title={v ?? ""}
                  >
                    {v}
                  </dd>
                </div>
              ))}
            </dl>
            {enCurso.motivo ? (
              <p className="mt-3 rounded bg-muted px-2 py-1.5 text-[12px] text-muted-foreground">
                {enCurso.motivo}
              </p>
            ) : null}
            <div className="mt-3">
              <Button
                disabled={decidir.isPending}
                onClick={() => conciliar(enCurso.movimientoId)}
              >
                Es este · conciliar
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Y SIEMPRE, la salida a mano. Es la que resuelve los casos que el motor
          no sabe mirar: el pago a plazos, el voucher sin leer, el abono con la
          glosa en blanco. */}
      <div className="mt-3">
        <Button
          size="sm"
          variant={caso.candidatos.length === 0 ? "default" : "outline"}
          onClick={() => setBuscando((x) => !x)}
        >
          Buscar el abono a mano
        </Button>
        {buscando ? (
          <BuscarMovimiento
            voucher={{
              importe: caso.vouchers[0]?.importe ?? null,
              moneda: caso.vouchers[0]?.moneda ?? caso.moneda,
              operacion: caso.vouchers[0]?.operacion ?? null,
              fechaPago: caso.vouchers[0]?.fechaPago ?? null,
            }}
            comprobanteId={caso.comprobanteId}
            totalBoleta={falta > 0 ? falta : caso.importe}
            alCerrar={() => setBuscando(false)}
          />
        ) : null}
      </div>

      <p className={cn("mt-3 text-[11.5px] text-muted-foreground")}>
        <Link
          to="/archivo/$id"
          params={{ id: caso.archivoId }}
          className="inline-flex items-center gap-1.5 underline underline-offset-2 hover:no-underline"
        >
          <FileText className="size-3.5" />
          Abrir el expediente entero
        </Link>
      </p>

      {/* El papel en grande. Va al final: es un diálogo a pantalla completa, no
          una parte de la comparación. */}
      <Visor papel={papel} alCerrar={() => setPapel(null)} />
    </div>
  );
}
